import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Spinner from '../../../components/Spinner';

import TravelSearchResult from './TravelSearchResult';
import TravelSearchMap from './TravelSearchMap';

const TravelSearchResults = (props) => {
  const [results, setResults] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [isCompressedList, setIsCompressedList] = useState(false);

  function getMaxPrice(products) {
    return Math.max(...products.map((product) => product.Price.Price));
  }
  function getMinPrice(products) {
    return Math.min(...products.map((product) => product.Price.Price));
  }
  function getMaxSize(products) {
    return Math.max(
      ...products.map((product) =>
        parseInt(props.getAttribute(product, 'Area'))
      )
    );
  }
  function getMinSize(products) {
    return Math.min(
      ...products.map((product) =>
        parseInt(props.getAttribute(product, 'Area'))
      )
    );
  }

  function countResults() {
    let count = 0;
    props.filterResults(results).forEach((result) => {
      if (result.products) {
        count += props.filterResults(result.products).length;
      } else {
        count++;
      }
    });
    return count;
  }

  // Calculate what value should be shown for beds info.
  function calculateBeds(persons, totalPersons, extraBeds) {
    let extra = extraBeds;

    // If extrabeds is zero or null, try to also calculate the
    // value from persons and total persons values.
    if (!extraBeds && persons !== totalPersons) {
      extra = totalPersons - persons;
    }

    let beds = `${persons}`;
    if (extra) {
      beds = `${persons}+${extra}`;
    }

    return <>{beds}</>;
  }

  useEffect(() => {
    const providersSorted = [];
    const groupedResults = props.products
      .map((product) => {
        if (
          product.accommodationProvider &&
          product.accommodationProvider.groupResults &&
          !providersSorted.includes(product.CompanyID)
        ) {
          // The results are to be shown as grouped. Get all the accommodations
          // with the accommodation providers id.
          const accommodationProviderProducts = props.products.filter(
            (accommodation) => accommodation.CompanyID === product.CompanyID
          );
          providersSorted.push(product.CompanyID);
          // Format the data like this for easier sorting.
          return {
            products: accommodationProviderProducts,
            accommodationProvider: product.accommodationProvider,
            Name: product.accommodationProvider.name,
            maxPrice: getMaxPrice(accommodationProviderProducts),
            minPrice: getMinPrice(accommodationProviderProducts),
            minSize: getMinSize(accommodationProviderProducts),
            maxSize: getMaxSize(accommodationProviderProducts),
          };
        } else if (
          product.accommodationProvider &&
          !product.accommodationProvider.groupResults
        ) {
          return product;
        }
      })
      .filter((result) => result !== undefined);

    setDefaults(
      props.defaults.filter((accommodationProvider) => {
        if (
          accommodationProvider.alwaysShow &&
          !providersSorted.includes(
            accommodationProvider.id &&
              groupedResults.find(
                (result) =>
                  accommodationProvider.id &&
                  result.CompanyID &&
                  result.CompanyID !== accommodationProvider.id
              )
          )
        ) {
          return accommodationProvider;
        }
      })
    );

    setResults(groupedResults);
  }, [props.products]);

  const travelSearchResults = props
    .filterResults(results)
    .sort(props.sortResults(props.sortType, props.sortOrder))
    .map((searchResult) => {
      return (
        searchResult.accommodationProvider && (
          <div key={searchResult.ProductID}>
            {searchResult.accommodationProvider.groupResults ? (
              <TravelSearchResult
                isCompressed={isCompressedList}
                additionalInfo={
                  searchResult.accommodationProvider.additionalInfo
                }
                title={searchResult.Name}
                numberOfResults={
                  props.filterResults(searchResult.products).length
                }
                bookingLink={props.getSearchUrl(
                  searchResult.accommodationProvider
                )}
                external={searchResult.accommodationProvider.external}
                image={{
                  url: searchResult.accommodationProvider.image,
                  description: searchResult.Name,
                }}
                custBody={
                  <>
                    {props
                      .filterResults(searchResult.products)
                      .sort(props.sortResults(props.sortType, props.sortOrder))
                      .slice(0, isCompressedList ? 2 : 3)
                      .map((product) => (
                        <div key={product.ProductID}>
                          <div>
                            <strong>{product.Name}</strong>
                          </div>
                          <div className="accommodation-search__result__specs-grouped">
                            <span>
                              {calculateBeds(
                                props.getAttribute(
                                  product,
                                  'Persons',
                                  'IntegerData'
                                ),
                                props.getAttribute(
                                  product,
                                  'TotalPersons',
                                  'IntegerData'
                                ),
                                props.getAttribute(
                                  product,
                                  'ExtraBeds',
                                  'IntegerData'
                                )
                              )}{' '}
                              {Drupal.t('beds')}
                            </span>
                            {!isCompressedList && (
                              <span>
                                {props.getAttribute(product, 'DistanceSkiLift')}{' '}
                                {Drupal.t('km to lift')}
                              </span>
                            )}
                            <span>
                              {product.Price
                                ? props.formatPrice(product.Price.Price)
                                : ''}
                              €
                            </span>
                          </div>
                        </div>
                      ))}
                  </>
                }
                companyName={searchResult.products[0].CompanyName}
                housingType={props.getAttribute(
                  searchResult.products[0],
                  'HousingType'
                )}
              />
            ) : (
              <TravelSearchResult
                key={searchResult.ProductID}
                isCompressed={isCompressedList}
                housingType={props.getAttribute(searchResult, 'HousingType')}
                additionalInfo={
                  searchResult.accommodationProvider.additionalInfo
                }
                bookingLink={props.getProductUrl(searchResult)}
                external={searchResult.accommodationProvider.external}
                area={parseInt(props.getAttribute(searchResult, 'Area'))}
                beds={calculateBeds(
                  props.getAttribute(searchResult, 'Persons', 'IntegerData'),
                  props.getAttribute(
                    searchResult,
                    'TotalPersons',
                    'IntegerData'
                  ),
                  props.getAttribute(searchResult, 'ExtraBeds', 'IntegerData')
                )}
                village_distance={props.getAttribute(
                  searchResult,
                  'DistanceRuka'
                )}
                lift_distance={props.getAttribute(
                  searchResult,
                  'DistanceSkiLift'
                )}
                skitrail_distance={props.getAttribute(
                  searchResult,
                  'DistanceCrossCountrySkiingTrack'
                )}
                price={
                  searchResult.Price
                    ? Number.parseFloat(searchResult.Price.Price).toFixed(0)
                    : null
                }
                title={searchResult.Name}
                image={{
                  url:
                    searchResult.DefaultImage !== undefined
                      ? searchResult.DefaultImage.Url
                      : '#',
                  description: searchResult.Name,
                }}
                companyName={searchResult.CompanyName}
              />
            )}
          </div>
        )
      );
    });

  // These are shown, even if no accommodations were returned from the api.
  const defaultResults = defaults.map((accommodationProvider) => (
    <TravelSearchResult
      key={accommodationProvider.id}
      alwaysShow={true}
      additionalInfo={accommodationProvider.additionalInfo}
      title={`${accommodationProvider.name}`}
      bookingLink={props.getSearchUrl(accommodationProvider)}
      external={accommodationProvider.external}
      isCompressed={isCompressedList}
      linkText={Drupal.t('Read more', {}, { context: 'Travelsearch' })}
      custBody={
        <div
          dangerouslySetInnerHTML={{
            __html: accommodationProvider.body,
          }}
        ></div>
      }
      image={{
        url: accommodationProvider.image,
        description: accommodationProvider.name,
      }}
    />
  ));

  return (
    <div
      className={`accommodation-search__results_wrapper ${
        isCompressedList ? 'travel-search__compressed' : ''
      }`}
    >
      {!props.showMap && (
        <div className="accommodation-search__results_header_wrapper">
          <div className="accommodation-search__results_count">
            {results.length > 0 &&
              `${countResults()} ${Drupal.t('accommodations')}`}
          </div>

          <div className="accommodation-search__results_list_controls">
            <div className="accommodation-search__results_sort">
              <label>{Drupal.t('Sort by', {}, { context: 'search' })}: </label>
              <div className="form-item__dropdown form-item__dropdown--inline">
                <select
                  disabled={props.isSortDisabled}
                  onChange={(e) => {
                    props.setSortType(e.target.value);
                    props.setSortOrder(
                      e.target.options[e.target.selectedIndex].dataset.order
                    );
                  }}
                  className="form-item__select form-item__select--inline"
                >
                  <option value="random" data-order="asc">
                    {Drupal.t('Random')}
                  </option>
                  <option value="field_area_m2" data-order="asc">
                    {Drupal.t('Size, ascending')}
                  </option>
                  <option value="field_area_m2" data-order="desc">
                    {Drupal.t('Size, descending')}
                  </option>
                  <option value="field_title">{Drupal.t('Name')}</option>
                  <option value="price" data-order="asc">
                    {Drupal.t('Price, ascending')}
                  </option>
                  <option value="price" data-order="desc">
                    {Drupal.t('Price, descending')}
                  </option>
                </select>
              </div>
            </div>

            <div className="accommodation-search__results_list_type">
              <label className="visually-hidden">
                {Drupal.t('List type', {}, { context: 'search' })}:{' '}
              </label>
              <div className="form-item--radio form-item--radio__item">
                <input
                  className="form-item--radio__input visually-hidden"
                  type="radio"
                  name="type"
                  id="full"
                  value="full"
                  defaultChecked
                  onChange={() => {
                    setIsCompressedList(false);
                  }}
                />
                <label
                  htmlFor="full"
                  className="form-item__label form-item__label--radio"
                >
                  <span className="visually-hidden">
                    {Drupal.t(
                      'Show more information for a result',
                      {},
                      { context: 'search' }
                    )}
                  </span>
                </label>
              </div>
              <div className="form-item--radio form-item--radio__item">
                <input
                  className="form-item--radio__input visually-hidden"
                  type="radio"
                  name="type"
                  id="compressed"
                  value="compressed"
                  onChange={() => {
                    setIsCompressedList(true);
                  }}
                />
                <label
                  htmlFor="compressed"
                  className="form-item__label form-item__label--radio"
                >
                  <span className="visually-hidden">
                    {Drupal.t(
                      'Show less information for a result',
                      {},
                      { context: 'search' }
                    )}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <button onClick={props.toggleMap} className="button button--map">
            {props.showMap ? '' : Drupal.t('Show on map')}
          </button>
        </div>
      )}
      {countResults() === 0 && defaultResults.length === 0 ? (
        <div className="accommodation-search__no-results">
          <h3>{Drupal.t('No results')}</h3>
        </div>
      ) : (
        <>
          {!props.showMap && (
            <>
              {travelSearchResults}

              {defaultResults}
            </>
          )}
          {/* Render the map view. */}
          {props.showMap && (
            <TravelSearchMap
              results={props
                .filterResults(props.products)
                .filter((result) => result.Latitude && result.Longitude)
                .map((result, i) => {
                  return {
                    position: {
                      latitude: result.Latitude,
                      longitude: result.Longitude,
                    },
                    price: props.formatPrice(result.Price.Price),
                    content: (
                      <TravelSearchResult
                        key={i}
                        housingType={props.getAttribute(result, 'HousingType')}
                        additionalInfo={
                          result.accommodationProvider.additionalInfo
                        }
                        bookingLink={props.getProductUrl(result)}
                        external={result.accommodationProvider.external}
                        area={parseInt(props.getAttribute(result, 'Area'))}
                        beds={calculateBeds(
                          props.getAttribute(result, 'Persons', 'IntegerData'),
                          props.getAttribute(
                            result,
                            'TotalPersons',
                            'IntegerData'
                          ),
                          props.getAttribute(result, 'ExtraBeds', 'IntegerData')
                        )}
                        price={
                          result.Price
                            ? Number.parseFloat(result.Price.Price).toFixed(0)
                            : null
                        }
                        title={result.Name}
                        image={{
                          url:
                            result.DefaultImage !== undefined
                              ? result.DefaultImage.Url
                              : '#',
                          description: result.Name,
                        }}
                        companyName={result.CompanyName}
                      />
                    ),
                  };
                })}
            />
          )}
        </>
      )}
      {props.isSortDisabled && <Spinner />}
    </div>
  );
};

TravelSearchResults.propTypes = {
  accommodationProviders: PropTypes.array,
  products: PropTypes.array,
  defaults: PropTypes.array,
  filterResults: PropTypes.func,
  getSearchUrl: PropTypes.func,
  sortResults: PropTypes.func,
  sortType: PropTypes.string,
  sortOrder: PropTypes.string,
  getAttribute: PropTypes.func,
  formatPrice: PropTypes.func,
  setSortOrder: PropTypes.func,
  setSortType: PropTypes.func,
  getProductUrl: PropTypes.func,
  isSortDisabled: PropTypes.bool,
  showMap: PropTypes.bool,
  toggleMap: PropTypes.func,
};

export default TravelSearchResults;
